import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import {
  Box, Stack, Typography, IconButton, Chip, Tooltip, Button, Alert, CircularProgress,
  Collapse, Tabs, Tab, TextField, MenuItem, alpha,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import Drawer from '../ManagedModal';
import AuthContext from '../auth/Context';
import fetchXacThuc, { loiPhien } from '../auth/fetchXacThuc';

/**
 * LichSuChay — ngăn "Lịch sử chạy" của FlowEditor (đọc từ API flow/lich-su, collection flowrun).
 *
 * Trả lời câu hỏi "flow có chạy không, chạy tới đâu, hỏng ở đâu":
 *   - Tình trạng: instance đang sống hay không, đang chờ gì (kênh sự kiện / webhook / lịch), số lượt 24 giờ.
 *   - Gợi ý chẩn đoán: đang nghe nhưng chưa nhận sự kiện nào; sự kiện tới nhưng bị điều kiện loại; lỗi gần nhất.
 *   - Danh sách LƯỢT (mỗi lần kích hoạt): trạng thái, nguồn, thời gian; bấm để xem từng bước (vào/ra/lỗi).
 *   - Tab "Khởi động / dừng": vòng đời instance kèm lý do.
 */

const NHAN_LUOT = {
  running: { nhan: 'Đang chạy', mau: 'info' },
  completed: { nhan: 'Hoàn thành', mau: 'success' },
  failed: { nhan: 'Lỗi', mau: 'error' },
  cancelled: { nhan: 'Đã huỷ', mau: 'default' },
  bo_qua: { nhan: 'Bỏ qua', mau: 'warning' },
};
const NHAN_PHIEN = {
  started: { nhan: 'Khởi động', mau: 'success' },
  stopped: { nhan: 'Dừng', mau: 'default' },
  failed: { nhan: 'Hỏng', mau: 'error' },
  completed: { nhan: 'Kết thúc', mau: 'info' },
  o_tien_trinh_khac: { nhan: 'Ở tiến trình khác', mau: 'warning' },
  nha_khoa: { nhan: 'Nhả quyền mồ côi', mau: 'warning' },
};
const LOC = [
  { v: '', nhan: 'Tất cả' }, { v: 'failed', nhan: 'Lỗi' }, { v: 'bo_qua', nhan: 'Bỏ qua' },
  { v: 'completed', nhan: 'Hoàn thành' }, { v: 'running', nhan: 'Đang chạy' },
];
const TU_LAM_MOI_MS = 15000;

const gio = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString('vi-VN', { hour12: false }); } catch (e) { return String(d); }
};
const thoiLuong = (ms) => {
  if (ms == null || isNaN(ms)) return '';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} giây`;
  return `${Math.round(ms / 60000)} phút`;
};
const dep = (s) => {
  if (s == null || s === '') return '';
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch (e) { return String(s); }
};

const KhoiDuLieu = ({ nhan, noiDung }) => {
  const [mo, setMo] = useState(false);
  if (!noiDung) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Button size="small" onClick={() => setMo((v) => !v)} sx={{ textTransform: 'none', fontSize: 11, p: 0, minWidth: 0 }}>
        {mo ? '▾' : '▸'} {nhan}
      </Button>
      <Collapse in={mo} unmountOnExit>
        <Box component="pre" sx={{
          m: 0, mt: 0.5, p: 1, fontSize: 11, maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          bgcolor: (t) => alpha(t.palette.text.primary, 0.04), borderRadius: 1,
        }}>{dep(noiDung)}</Box>
      </Collapse>
    </Box>
  );
};

const DongLuot = ({ luot, taiChiTiet, onFocusNode }) => {
  const [mo, setMo] = useState(false);
  const [chiTiet, setChiTiet] = useState(null);
  const [dangTai, setDangTai] = useState(false);
  const [loi, setLoi] = useState('');
  const nhan = NHAN_LUOT[luot.trang_thai] || { nhan: luot.trang_thai, mau: 'default' };

  const bat = async () => {
    const moi = !mo;
    setMo(moi);
    if (moi && !chiTiet) {
      setDangTai(true); setLoi('');
      try { setChiTiet(await taiChiTiet(luot._id)); } catch (e) { if (!e.phienKetThuc) setLoi(e.message || String(e)); }
      setDangTai(false);
    }
  };
  const buoc = (chiTiet && chiTiet.buoc) || luot.buoc || [];

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box onClick={bat} sx={{ px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={nhan.nhan} color={nhan.mau} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{gio(luot.bat_dau)}</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {luot.so_buoc || 0} bước{luot.thoi_gian_ms != null ? ` · ${thoiLuong(luot.thoi_gian_ms)}` : ''}
          </Typography>
          {mo ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
        </Stack>
        {luot.nguon && <Typography variant="caption" color="text.secondary" component="div" noWrap>Nguồn: {luot.nguon}</Typography>}
        {luot.tien_trinh && <Typography variant="caption" color="text.secondary" component="div" noWrap title="Tiến trình (container) đã chạy lượt này">Chạy ở: {luot.tien_trinh}</Typography>}
        {(luot.loi || (luot.trang_thai === 'bo_qua' && luot.ghi_chu)) && (
          <Typography variant="caption" component="div" sx={{ color: luot.loi ? 'error.main' : 'warning.dark', wordBreak: 'break-word' }}>
            {luot.loi || luot.ghi_chu}
          </Typography>
        )}
      </Box>
      <Collapse in={mo} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {dangTai && <CircularProgress size={16} />}
          {loi && <Alert severity="error" sx={{ py: 0 }}>{loi}</Alert>}
          {buoc.map((b, i) => {
            const nb = NHAN_LUOT[b.trang_thai] || { nhan: b.trang_thai, mau: 'default' };
            return (
              <Box key={i} sx={{ pl: 1.5, py: 0.75, borderLeft: '2px solid', borderColor: (t) => {
                const p = t.palette[nb.mau]; return p && p.main ? p.main : t.palette.divider;
              } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                    {i + 1}. {b.ten || b.node_id} <Typography component="span" variant="caption" color="text.secondary">({b.type})</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{nb.nhan}{b.thoi_gian_ms != null ? ` · ${thoiLuong(b.thoi_gian_ms)}` : ''}</Typography>
                  {onFocusNode && (
                    <Tooltip title="Xem node trên sơ đồ">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onFocusNode(b.node_id); }} sx={{ p: '2px' }}>
                        <CenterFocusStrongRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                {b.loi && (
                  <Typography variant="caption" component="div" sx={{ color: b.trang_thai === 'bo_qua' ? 'warning.dark' : 'error.main', wordBreak: 'break-word' }}>
                    {b.loi}
                  </Typography>
                )}
                <KhoiDuLieu nhan="Dữ liệu vào" noiDung={b.dau_vao} />
                <KhoiDuLieu nhan="Kết quả" noiDung={b.dau_ra} />
              </Box>
            );
          })}
          {chiTiet && !buoc.length && <Typography variant="caption" color="text.secondary">Lượt này không có bước nào.</Typography>}
        </Box>
      </Collapse>
    </Box>
  );
};

/** Gợi ý chẩn đoán từ tình trạng hiện tại + các lượt gần nhất. */
function goiY(dl) {
  if (!dl) return [];
  const ra = [];
  const luot = dl.luot || [];
  const phienMoi = (dl.phien || [])[0];
  if (dl.ghi_lich_su === false) ra.push({ sev: 'warning', text: 'Máy chủ đang TẮT ghi lịch sử chạy (configs.flow.lichSuChay = false).' });
  if (dl.flow && dl.flow.tu_chay_loi) {
    const l = dl.flow.tu_chay_loi;
    ra.push({ sev: 'error', text: `Lần tự chạy gần nhất không thành: ${l.loi || JSON.stringify(l)}${l.luc ? ` (${gio(l.luc)})` : ''}` });
  }
  if (!dl.dang_chay) {
    const lyDo = phienMoi && phienMoi.trang_thai !== 'started' ? ` Lần gần nhất: ${(NHAN_PHIEN[phienMoi.trang_thai] || {}).nhan || phienMoi.trang_thai} lúc ${gio(phienMoi.bat_dau)}${phienMoi.ghi_chu ? ` — ${phienMoi.ghi_chu}` : ''}.` : '';
    ra.push({ sev: 'warning', text: `Flow đang KHÔNG chạy nên sẽ không nhận sự kiện nào. Bấm "Chạy" để flow bắt đầu lắng nghe.${lyDo}` });
    return ra;
  }
  if (dl.dang_chay.cluster_khac && dl.chu_so_huu) {
    ra.push({ sev: 'info', text: `Flow đang chạy ở tiến trình "${dl.chu_so_huu}" (không phải tiến trình trả lời trang này). Log của flow nằm ở container đó.` });
  }
  const phienKhoiDong = (dl.phien || []).find((p) => p.trang_thai === 'started');
  if (!phienKhoiDong) {
    ra.push({ sev: 'warning', text: 'Flow được đánh dấu đang chạy nhưng lịch sử chưa có lần khởi động nào — có thể tiến trình đang giữ quyền chạy là bản cũ (chưa cập nhật) hoặc giữ quyền mà không chạy. Kiểm tra mọi container đã được build lại; bấm Dừng rồi Chạy lại.' });
  }
  const batDau = phienMoi && phienMoi.trang_thai === 'started' ? new Date(phienMoi.bat_dau).getTime() : 0;
  const sauKhoiDong = luot.filter((l) => new Date(l.bat_dau).getTime() >= batDau);
  if (dl.dang_cho && !sauKhoiDong.length) {
    ra.push({ sev: 'info', text: `Flow đang ${dl.dang_cho}${batDau ? ` từ ${gio(batDau)}` : ''} nhưng CHƯA nhận được lần kích hoạt nào. Nếu bạn đã thao tác (vd. tạo đơn) mà không thấy lượt mới: nơi phát sự kiện chưa phát, phát sai kênh, hoặc máy chủ chưa được cập nhật bản mới.` });
  }
  if (luot.length && luot.every((l) => l.trang_thai === 'bo_qua')) {
    ra.push({ sev: 'warning', text: 'Sự kiện CÓ tới nhưng các lượt gần đây đều bị bỏ qua vì điều kiện của node kích hoạt — kiểm tra lại "condition_script".' });
  }
  const loiMoi = luot.find((l) => l.trang_thai === 'failed');
  if (loiMoi && luot[0] === loiMoi) ra.push({ sev: 'error', text: `Lượt gần nhất lỗi: ${loiMoi.loi || ''}` });
  return ra;
}

const LichSuChay = ({ open, onClose, apiUrl, idApp, accessToken, trustKey, flowId, onFocusNode }) => {
  const [tab, setTab] = useState(0);
  const [loc, setLoc] = useState('');
  const [dl, setDl] = useState(null);
  const [them, setThem] = useState([]);     // các trang "tải thêm"
  const [hetTrang, setHetTrang] = useState(false);
  const [dangTai, setDangTai] = useState(false);
  const [loi, setLoi] = useState('');
  const huy = useRef(false);

  const q = `access_token=${accessToken}&trustkey=${trustKey}`;
  // Token hết hạn: fetchXacThuc tự làm mới + chạy lại; không cứu được thì mở form đăng nhập, không hiện lỗi token.
  const ctxAuth = useContext(AuthContext);
  const apis = (ctxAuth && ctxAuth.apis) || null;
  const goi = useCallback(async (duongDan) => {
    const res = await fetchXacThuc(apis, `${apiUrl}/api/${idApp}/flow/${duongDan}${duongDan.includes('?') ? '&' : '?'}${q}`);
    if (res.phienKetThuc) throw loiPhien();
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.error) throw new Error(body.message || body.error || `Lỗi ${res.status}`);
    return body;
  }, [apiUrl, idApp, q, apis]);

  const taiLai = useCallback(async (imLang) => {
    if (!flowId) return;
    if (!imLang) setDangTai(true);
    try {
      const body = await goi(`lich-su/${flowId}?limit=30${loc ? `&trang_thai=${loc}` : ''}`);
      if (huy.current) return;
      setDl(body); setThem([]); setHetTrang((body.luot || []).length < 30); setLoi('');
    } catch (e) { if (!huy.current && !e.phienKetThuc) setLoi(e.message || String(e)); }
    if (!imLang && !huy.current) setDangTai(false);
  }, [flowId, loc, goi]);

  useEffect(() => {
    huy.current = false;
    if (!open) return undefined;
    taiLai(false);
    return () => { huy.current = true; };
  }, [open, taiLai]);

  // Tự làm mới trang đầu khi đang mở (không đụng tới các trang "tải thêm").
  useEffect(() => {
    if (!open || them.length) return undefined;
    const t = setInterval(() => taiLai(true), TU_LAM_MOI_MS);
    return () => clearInterval(t);
  }, [open, them.length, taiLai]);

  const taiThem = async () => {
    const tatCa = [...((dl && dl.luot) || []), ...them];
    const cuoi = tatCa[tatCa.length - 1];
    if (!cuoi) return;
    setDangTai(true);
    try {
      const body = await goi(`lich-su/${flowId}?limit=30${loc ? `&trang_thai=${loc}` : ''}&truoc=${encodeURIComponent(new Date(cuoi.bat_dau).toISOString())}`);
      setThem((v) => [...v, ...(body.luot || [])]);
      setHetTrang((body.luot || []).length < 30);
    } catch (e) { if (!e.phienKetThuc) setLoi(e.message || String(e)); }
    setDangTai(false);
  };
  const taiChiTiet = useCallback((id) => goi(`lich-su-luot/${id}`), [goi]);

  const dsLuot = [...((dl && dl.luot) || []), ...them];
  const tk = (dl && dl.thong_ke_24h) || {};
  const tongTk = Object.values(tk).reduce((a, b) => a + b, 0);
  const canhBao = goiY(dl);

  return (
    <Drawer type="drawer" anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <HistoryRoundedIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }} noWrap>
          Lịch sử chạy{dl && dl.flow && dl.flow.title ? ` — ${dl.flow.title}` : ''}
        </Typography>
        <Tooltip title="Làm mới">
          <span><IconButton size="small" onClick={() => taiLai(false)} disabled={dangTai}><RefreshRoundedIcon fontSize="small" /></IconButton></span>
        </Tooltip>
        <IconButton size="small" onClick={onClose}><CloseRoundedIcon fontSize="small" /></IconButton>
      </Box>

      {!flowId ? (
        <Box sx={{ p: 2 }}><Alert severity="info">Hãy lưu flow trước — lịch sử chỉ có cho flow đã lưu.</Alert></Box>
      ) : (
        <>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" sx={{ fontWeight: 700 }}
                color={dl && dl.dang_chay ? 'success' : 'default'}
                label={dl ? (dl.dang_chay ? (dl.dang_chay.status === 'listening' ? 'Đang lắng nghe' : 'Đang chạy') : 'Không chạy') : '…'} />
              {tongTk > 0 && <Typography variant="caption" color="text.secondary">24 giờ qua: {tongTk} lượt</Typography>}
              {Object.entries(tk).map(([k, n]) => (
                <Chip key={k} size="small" variant="outlined" color={(NHAN_LUOT[k] || {}).mau || 'default'}
                  label={`${(NHAN_LUOT[k] || {}).nhan || k}: ${n}`} sx={{ height: 20, fontSize: 11 }} />
              ))}
            </Stack>
            {dl && dl.chu_so_huu && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                Tiến trình giữ quyền chạy: {dl.chu_so_huu}{dl.tien_trinh_nay && dl.chu_so_huu === dl.tien_trinh_nay ? ' (chính tiến trình này)' : ''}
              </Typography>
            )}
            {dl && dl.dang_cho && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Kích hoạt bởi: {dl.dang_cho}
              </Typography>
            )}
          </Box>
          {loi && <Alert severity="error" onClose={() => setLoi('')} sx={{ borderRadius: 0 }}>{loi}</Alert>}
          {canhBao.map((c, i) => <Alert key={i} severity={c.sev} sx={{ borderRadius: 0, py: 0, fontSize: 12 }}>{c.text}</Alert>)}

          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth" sx={{ minHeight: 36, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}>
            <Tab label="Lượt chạy" />
            <Tab label="Khởi động / dừng" />
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {tab === 0 && (
              <>
                <Box sx={{ px: 1.5, py: 1 }}>
                  <TextField select size="small" label="Lọc" value={loc} onChange={(e) => setLoc(e.target.value)} sx={{ minWidth: 160 }}>
                    {LOC.map((o) => <MenuItem key={o.v} value={o.v}>{o.nhan}</MenuItem>)}
                  </TextField>
                </Box>
                {dangTai && !dl && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
                {dl && !dsLuot.length && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Chưa có lượt chạy nào{loc ? ' khớp bộ lọc' : ''}.</Typography>
                )}
                {dsLuot.map((l) => <DongLuot key={l._id} luot={l} taiChiTiet={taiChiTiet} onFocusNode={onFocusNode} />)}
                {dl && dsLuot.length > 0 && !hetTrang && (
                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Button size="small" onClick={taiThem} disabled={dangTai}>Tải thêm</Button>
                  </Box>
                )}
              </>
            )}
            {tab === 1 && (
              <>
                {dl && !(dl.phien || []).length && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Chưa có ghi nhận khởi động / dừng.</Typography>
                )}
                {((dl && dl.phien) || []).map((p) => {
                  const n = NHAN_PHIEN[p.trang_thai] || { nhan: p.trang_thai, mau: 'default' };
                  return (
                    <Box key={p._id} sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={n.nhan} color={n.mau} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{gio(p.bat_dau)}</Typography>
                      </Stack>
                      {p.ghi_chu && <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>{p.ghi_chu}</Typography>}
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default LichSuChay;
export { goiY };
